import pluralize from 'pluralize';

import { ONBOARDING_INTRO_IMAGE_DIR } from '~/components/v2/components/workspace/onboarding-intro/constants/onboardingIntro';
import { WORKSPACE_AGENT_PANEL_ACTIONS_MENU_CHIP } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';

/** Sent when a prompt card is pressed until each card has its own prompt. */
export const WORKSPACE_AGENT_IMPROVED_NULL_STATE_PROMPT = 'hello';

/** Shown on Update custom fields when the workspace has no custom fields yet. */
export const WORKSPACE_AGENT_UPDATE_CUSTOM_FIELDS_SAMPLE_FIELD_NAME = 'Status';

/** Back, middle, front. */
export const WORKSPACE_AGENT_PROMPT_CARD_IMAGE_STACK_POSITIONS = ['back', 'middle', 'front'] as const;

/** Cards without a live search still need three thumbnails. These are a fixed pick from the intro library. */
const exampleThumbnail = (fileName: string) => `${ONBOARDING_INTRO_IMAGE_DIR}/${fileName}`;

export const WORKSPACE_AGENT_PROMPT_CARD_IMAGE_COUNT = WORKSPACE_AGENT_PROMPT_CARD_IMAGE_STACK_POSITIONS.length;

/**
 * The three chips that fit the empty thread once prompt cards sit above them.
 * Order is the on-screen order.
 */
export const WORKSPACE_AGENT_IMPROVED_NULL_STATE_CHIP_IDS = [
  'hero-summarize-status',
  'hero-artist-variants',
  WORKSPACE_AGENT_PANEL_ACTIONS_MENU_CHIP.id,
] as const;

type WorkspaceAgentPromptCardBase = {
  buttonLabel: string;
  title: string;
};

export type WorkspaceAgentPromptCardLiveSubtitleContext = {
  boardName?: string;
  staleSinceYear?: number;
};

/** Subtitle comes from clip search at runtime; `getSubtitle` is the only copy. */
type WorkspaceAgentPromptCardWithLiveCount = WorkspaceAgentPromptCardBase & {
  getSubtitle: (count: number, context?: WorkspaceAgentPromptCardLiveSubtitleContext) => string;
  id: 'prompt-card-tag-new-uploads' | 'prompt-card-tag-product-shots' | 'prompt-card-unused-assets';
};

type WorkspaceAgentPromptCardStatic = WorkspaceAgentPromptCardBase & {
  getSubtitle: () => string;
  id:
    | 'prompt-card-export-cdn-links'
    | 'prompt-card-find-duplicates'
    | 'prompt-card-old-logo'
    | 'prompt-card-rename-convention'
    | 'prompt-card-resize-board'
    | 'prompt-card-set-up-custom-fields'
    | 'prompt-card-update-custom-fields';
  /** Front first. */
  thumbnailUrls: readonly string[];
};

export type WorkspaceAgentPromptCardDefinition = WorkspaceAgentPromptCardWithLiveCount | WorkspaceAgentPromptCardStatic;

export const WORKSPACE_AGENT_PROMPT_CARDS: readonly WorkspaceAgentPromptCardDefinition[] = [
  {
    id: 'prompt-card-rename-convention',
    title: 'Match your naming convention',
    buttonLabel: 'Rename',
    getSubtitle: () => "Files don't match yet",
    thumbnailUrls: [
      exampleThumbnail('library-blush-closeup.jpg'),
      exampleThumbnail('resize-portrait.webp'),
      exampleThumbnail('library-orange-bottle.jpg'),
    ],
  },
  {
    id: 'prompt-card-tag-product-shots',
    title: 'Tag product shots',
    buttonLabel: 'Tag them',
    getSubtitle: (count: number, context?: WorkspaceAgentPromptCardLiveSubtitleContext) =>
      context?.boardName
        ? `${count.toLocaleString()} untagged in "${context.boardName}"`
        : `${count.toLocaleString()} untagged`,
  },
  {
    id: 'prompt-card-old-logo',
    title: 'Find the old logo',
    buttonLabel: 'Find them',
    getSubtitle: () => 'Catch it before it ships',
    thumbnailUrls: [
      exampleThumbnail('library-contact-sheet.jpg'),
      exampleThumbnail('resize-landscape.jpg'),
      exampleThumbnail('resize-square.webp'),
    ],
  },
  {
    id: 'prompt-card-resize-board',
    title: 'Resize for social',
    buttonLabel: 'Resize',
    getSubtitle: () => '1:1, 4:5 and 9:16 in one run',
    thumbnailUrls: [
      exampleThumbnail('library-flatlay.jpg'),
      exampleThumbnail('library-blush-recline.jpg'),
      exampleThumbnail('library-pink-tube.jpg'),
    ],
  },
  {
    id: 'prompt-card-set-up-custom-fields',
    title: 'Set up custom fields',
    buttonLabel: 'Set up',
    getSubtitle: () => 'No trip to settings needed',
    thumbnailUrls: [
      exampleThumbnail('library-jar-portrait.jpg'),
      exampleThumbnail('resize-original.webp'),
      exampleThumbnail('library-perfume-bottle.jpg'),
    ],
  },
  {
    id: 'prompt-card-unused-assets',
    title: 'Find unused assets',
    buttonLabel: 'Clean up',
    getSubtitle: (count: number, context?: WorkspaceAgentPromptCardLiveSubtitleContext) => {
      const base = `You have ${count.toLocaleString()} unused ${pluralize('asset', count)}`;

      return context?.staleSinceYear !== undefined ? `${base} from ${context.staleSinceYear}.` : `${base}.`;
    },
  },
  {
    id: 'prompt-card-update-custom-fields',
    title: 'Update custom fields',
    buttonLabel: 'Update',
    getSubtitle: () => `Missing a ${WORKSPACE_AGENT_UPDATE_CUSTOM_FIELDS_SAMPLE_FIELD_NAME}`,
    thumbnailUrls: [
      exampleThumbnail('library-wordmark-face.jpg'),
      exampleThumbnail('library-blush-cream.jpg'),
      exampleThumbnail('library-tube-nozzle.jpg'),
    ],
  },
  {
    id: 'prompt-card-find-duplicates',
    title: 'Find duplicates across libraries',
    buttonLabel: 'Review',
    getSubtitle: () => 'Likely duplicates',
    thumbnailUrls: [
      exampleThumbnail('library-perfume-silhouette.jpg'),
      exampleThumbnail('library-cap-lift.jpg'),
      exampleThumbnail('resize-reel.webp'),
    ],
  },
  {
    id: 'prompt-card-export-cdn-links',
    title: 'Export CDN links',
    buttonLabel: 'Export',
    getSubtitle: () => 'Evergreen links in one go',
    thumbnailUrls: [
      exampleThumbnail('library-palette.jpg'),
      exampleThumbnail('library-cheek-tube.jpg'),
      exampleThumbnail('library-hand-cream.jpg'),
    ],
  },
  {
    id: 'prompt-card-tag-new-uploads',
    title: 'Tag new uploads',
    buttonLabel: 'Suggest',
    getSubtitle: (count: number) => `${count.toLocaleString()} uploaded this week`,
  },
];

export const pickWorkspaceAgentImprovedNullStateChips = <TChip extends { id: string }>(chips: readonly TChip[]) =>
  WORKSPACE_AGENT_IMPROVED_NULL_STATE_CHIP_IDS.flatMap((id) => {
    const chip = chips.find((candidate) => candidate.id === id);

    return chip ? [chip] : [];
  });
